import type { RouteInterface } from '@/src/common/enums/routes'
import type { CardApplicationStatus } from '@acre-rewards/lib'
import { z } from 'zod/v4'

export const Routes = {
  CardOnboarding: {
    route: '/main/card-application',
    name: 'Open Card Application',
    availableForDeepLinking: false,
  },
  VerifyOTPCardApplication: {
    route: '/main/card-application/verifyOtpCardApplication',
    name: 'Verify OTP Card Application',
    availableForDeepLinking: false,
  },
  OfferCode: {
    route: '/main/card-application/offerCode',
    name: 'Offer Code',
    availableForDeepLinking: false,
  },
  FindYourHome: {
    route: '/main/card-application/findYourHome',
    name: 'Find Your Home',
    availableForDeepLinking: false,
  },
  LastQuestion: {
    route: '/main/card-application/lastQuestion',
    name: 'Last Question',
    availableForDeepLinking: false,
  },
  ApplicationCardHolderAgreement: {
    route: '/main/card-application/applicationCardHolderAgreement',
    name: 'Card Holder Agreement',
    availableForDeepLinking: false,
  },
  ApplicationConfirmation: {
    route: '/main/card-application/applicationConfirmation',
    name: 'Application Confirmation',
    availableForDeepLinking: false,
  },
  ApplicationProcessing: {
    route: '/main/card-application/applicationProcessing',
    name: 'Application Being Processed',
    availableForDeepLinking: true,
  },
  ApplicationApproved: {
    route: '/main/card-application/applicationApproved',
    name: 'Card Application Approved',
    availableForDeepLinking: false,
  },
  ApplicationCreditFrozen: {
    route: '/main/card-application/applicationCreditFrozen',
    name: 'Application Credit Frozen',
    availableForDeepLinking: false,
  },
  ApplicationDenied: {
    route: '/main/card-application/applicationDenied',
    name: 'Application Denied',
    availableForDeepLinking: false,
  },
  DocumentUpload: {
    route: '/main/card-application/documentUpload',
    name: 'Document Upload',
    availableForDeepLinking: false,
  },
  ApplicationInReview: {
    route: '/main/card-application/applicationInReview',
    name: 'Documents in Review',
    availableForDeepLinking: false,
  },
  ApplicationManualReview: {
    route: '/main/card-application/applicationManualReview',
    name: 'Manual Review',
    availableForDeepLinking: false,
  },
} as const satisfies Record<string, RouteInterface>

export const ApplicationStatusRoutes = {
  // Even if the card is ISSUED, we want to bring the user to the application processing page because,
  // assuming the user hasn't seen the `ApplicationApproved` screen yet (as is implied by the fact that
  // they're on the `cardBenefits` screen), they'll be redirected to the `ApplicationApproved` screen,
  // which is only navigable from the `ApplicationProcessing` screen. Also, it's fine for the user to
  // see the `ApplicationProcessing`+`ApplicationApproved` screen multiple times, but it _is not_ ok
  // for the user to jump directly to the `CardManagement`, without ever seeing the `ApplicationProcessing`
  // (where they would be informed of their credit limit, APR, etc.)
  SUBMITTING: { entryRoute: Routes.ApplicationProcessing, allowableRoutes: [] },
  STARTED: { entryRoute: Routes.ApplicationProcessing, allowableRoutes: [] },
  PROCESSING: { entryRoute: Routes.ApplicationProcessing, allowableRoutes: [] },
  DOCUMENT_UPLOAD: {
    entryRoute: Routes.ApplicationManualReview,
    allowableRoutes: [Routes.DocumentUpload],
  },
  MANUAL_REVIEW_GENERIC: { entryRoute: Routes.ApplicationInReview, allowableRoutes: [] },
  FROZEN_CREDIT: { entryRoute: Routes.ApplicationCreditFrozen, allowableRoutes: [] },
  DENIED: { entryRoute: Routes.ApplicationDenied, allowableRoutes: [] },
  ISSUED: { entryRoute: Routes.ApplicationApproved, allowableRoutes: [] },
  APPROVAL_ACKED: { entryRoute: Routes.ApplicationApproved, allowableRoutes: [] },
} as const satisfies Record<
  Exclude<z.infer<typeof CardApplicationStatus>, 'ADMIN_RESET'>,
  { entryRoute: RouteInterface; allowableRoutes: RouteInterface[] }
>
